import {
    type MrtCompanion,
    useFetchMrtCompanionForCase,
} from "@/queries/useFetchMrtCompanionForCase.ts";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    Typography,
} from "@mui/material";
import { AlertCircle, Info } from "lucide-react";
import { RhfCheckbox } from "mui-rhf-integration";
import type React from "react";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

type Props = {
    caseUUID: string;
};

const AddNoteAddToCompanionSection: React.FC<Props> = ({ caseUUID }: Props) => {
    const form = useFormContext();
    const addNoteToCompanions = form.watch("addNoteToCompanions");
    const selectedCompanions = form.watch("selectedCompanions") || [];
    const hasInitialized = useRef(false);

    const selectedCompanionsError = form.formState.errors.selectedCompanions?.message;

    const {
        data: companions = [],
        isLoading,
        error,
    } = useFetchMrtCompanionForCase({
        caseId: caseUUID,
        columnFilters: [],
        globalFilter: "",
        pagination: {
            pageIndex: 0,
            pageSize: 100,
        },
        sorting: [],
    });

    const handleCompanionToggle = (companionId: string) => {
        const currentSelected = form.getValues("selectedCompanions") || [];
        const newSelected = currentSelected.includes(companionId)
            ? currentSelected.filter((id: string) => id !== companionId)
            : [...currentSelected, companionId];

        form.setValue("selectedCompanions", newSelected, { shouldValidate: true });
    };

    const handleSelectAll = () => {
        const allCompanionIds = companions.map((companion: MrtCompanion) => companion.idUUID);
        form.setValue("selectedCompanions", allCompanionIds, { shouldValidate: true });
    };

    const handleDeselectAll = () => {
        form.setValue("selectedCompanions", [], { shouldDirty: true, shouldValidate: true });
    };

    useEffect(() => {
        if (companions.length > 0 && !hasInitialized.current) {
            const allCompanionIds = companions.map((companion: MrtCompanion) => companion.idUUID);
            form.setValue("selectedCompanions", allCompanionIds, { shouldValidate: true });
            hasInitialized.current = true;
        }
    }, [companions, form]);

    return (
        <Box
            sx={{
                mt: 1,
                p: 2,
                backgroundColor: "#fafafa",
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} icon={<AlertCircle size={20} />}>
                    <Typography variant="body2">
                        Failed to load companion cases:{" "}
                        {error.message || "An unexpected error occurred"}
                    </Typography>
                </Alert>
            )}

            {companions.length > 0 && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        <Info size={20} />
                        <Typography
                            sx={{
                                fontSize: "1rem",
                                fontWeight: 500,
                                color: "text.primary",
                            }}
                        >
                            Companion Cases
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <RhfCheckbox control={form.control} name="addNoteToCompanions" />
                            }
                            label="Add to Companion Cases"
                            sx={{ mb: 2 }}
                        />
                    </Box>
                </>
            )}

            {addNoteToCompanions && companions.length > 0 && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            mb: 2,
                            ml: 2,
                        }}
                    >
                        <Button
                            size="small"
                            onClick={handleSelectAll}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.875rem",
                            }}
                        >
                            Select All
                        </Button>
                        <Button
                            size="small"
                            onClick={handleDeselectAll}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.875rem",
                            }}
                        >
                            Deselect All
                        </Button>
                    </Box>

                    <Box sx={{ ml: 2 }}>
                        {companions.map((companion) => {
                            const companionId = companion.idUUID;

                            // get the name of the person that is not the current case
                            const companionCaseId =
                                companion.idCase1?.idUUID === caseUUID
                                    ? companion.idCase2?.id
                                    : companion.idCase1?.id;
                            const companionName =
                                companion.idCase1?.idUUID === caseUUID
                                    ? companion.idCase2?.clientName
                                    : companion.idCase1?.clientName;

                            return (
                                <Box
                                    key={companionId}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: (theme) => theme.spacing(1),
                                        padding: (theme) => theme.spacing(1),
                                        backgroundColor: (theme) => theme.palette.background.paper,
                                        borderRadius: (theme) => theme.shape.borderRadius,
                                        border: "1px solid #ccc",
                                        mb: 1,
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedCompanions.includes(companionId)}
                                        onChange={() => handleCompanionToggle(companionId)}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            Case #{companionCaseId}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {companionName}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}

                        {selectedCompanionsError && typeof selectedCompanionsError === "string" && (
                            <FormHelperText error sx={{ ml: 0, mt: 1 }}>
                                {selectedCompanionsError}
                            </FormHelperText>
                        )}
                    </Box>
                </>
            )}

            {companions.length === 0 && !isLoading && !error && (
                <Typography
                    sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        mt: 1,
                    }}
                >
                    No companion cases found.
                </Typography>
            )}

            {addNoteToCompanions && isLoading && (
                <Typography
                    sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        mt: 1,
                    }}
                >
                    Loading companion cases...
                </Typography>
            )}
        </Box>
    );
};

export default AddNoteAddToCompanionSection;
